import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi, salesHubApi } from '@/services/api';
import { formatMoney, getCurrencySymbol } from '@/utils/currency';
import toast from 'react-hot-toast';
import AgeVerificationModal from '@/components/pos/AgeVerificationModal';
import { useUIStore } from '@/store/uiStore';
import {
  X,
  Menu,
  Calendar,
  Search,
  User,
  Plus,
  MinusCircle,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Copy,
  Layers,
  LayoutGrid,
  Loader2,
  ShieldAlert,
  MessageSquare,
  Pencil,
} from 'lucide-react';
import CartLineNoteModal, { type CartLineNoteMode } from '@/components/pos/CartLineNoteModal';

export interface OrderLineItem {
  item: string; // POS Item primary key name (e.g. IT1359)
  item_code: string;
  item_name: string;
  line_comment?: string;
  uom: string;
  cost_price: number;
  base_price: number;
  rate: number; // sale price
  stock: number;
  quantity: number;
  vat_rate: number; // fraction e.g. 0.20 for 20%; from POS Item.tax_rate / 100 (0 for zero-rated)
  has_age_restriction?: boolean;
  minimum_age?: number;
  verified_birth_year?: number;
}

/** POS Item.tax_rate is a percent (0, 20, …); Sales Hub lines store a fraction. */
function vatRateFromItemTax(taxRate: unknown): number {
  const pct = Number(taxRate);
  if (!Number.isFinite(pct) || pct <= 0) return 0;
  return pct / 100;
}

interface CreateSalesOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'quote' | 'sale';
  editDocId?: string | null;
  initialData?: any | null;
  onSuccess?: (createdId: string, mode: 'quote' | 'sale') => void;
}

export default function CreateSalesOrderModal({
  isOpen,
  onClose,
  mode,
  editDocId,
  initialData,
  onSuccess,
}: CreateSalesOrderModalProps) {
  const queryClient = useQueryClient();
  const currencySymbol = getCurrencySymbol();
  const { toggleSidebar } = useUIStore();

  // Collapsible Sections
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(true);
  const [isDiscountsOpen, setIsDiscountsOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const [viewMode, setViewMode] = useState<'visual' | 'compact'>('visual');

  // Form Fields - Order Details (local calendar dates — avoid UTC off-by-one)
  const todayStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);
  const getDefaultExpiry = (baseDate?: string) => {
    try {
      const d = baseDate ? new Date(baseDate.length === 10 ? baseDate + 'T12:00:00' : baseDate) : new Date();
      d.setDate(d.getDate() + 30);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    } catch {
      return '';
    }
  };

  const [deliveryDate, setDeliveryDate] = useState<string>(todayStr);
  const [saleDate, setSaleDate] = useState<string>(todayStr);
  const [expiryDate, setExpiryDate] = useState<string>(() => getDefaultExpiry());
  const [orderType, setOrderType] = useState<string>('Delivery Price');
  const [paymentTerm, setPaymentTerm] = useState<string>('0 Days');
  const [paymentMethod, setPaymentMethod] = useState<string>('Select payment method');

  // Customer Fields
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [customerSearch, setCustomerSearch] = useState<string>('');
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState<boolean>(false);
  const [customerName, setCustomerName] = useState<string>('');
  const [selectedCustomerType, setSelectedCustomerType] = useState<string>('Retailer');
  const [isSelectedDefault, setIsSelectedDefault] = useState<boolean>(false);
  const [customerLimit, setCustomerLimit] = useState<number>(0);
  const [customerAvailable, setCustomerAvailable] = useState<number>(0);

  // Address Fields
  const [address, setAddress] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [postCode, setPostCode] = useState<string>('');
  const [deliverySameAsAbove, setDeliverySameAsAbove] = useState<boolean>(true);
  const [deliveryAddress, setDeliveryAddress] = useState<string>('');
  const [deliveryCity, setDeliveryCity] = useState<string>('');
  const [deliveryPostCode, setDeliveryPostCode] = useState<string>('');

  // Notes & Discount
  const [notes, setNotes] = useState<string>('');
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState<number>(0);

  // Line Items
  const [items, setItems] = useState<OrderLineItem[]>([]);
  const [lineNoteEdit, setLineNoteEdit] = useState<{ index: number; mode: CartLineNoteMode } | null>(null);

  // Age Verification
  const [verifiedCustomerBirthYear, setVerifiedCustomerBirthYear] = useState<number | null>(null);
  const [agePendingProduct, setAgePendingProduct] = useState<any | null>(null);

  // Populate form if in edit mode or reset on new
  useEffect(() => {
    if (isOpen && editDocId && initialData) {
      const invDate = initialData.invoice_date ? initialData.invoice_date.split(' ')[0] : todayStr;
      const expDate = initialData.valid_till
        ? initialData.valid_till.split(' ')[0]
        : initialData.expected_date
        ? initialData.expected_date.split(' ')[0]
        : getDefaultExpiry(invDate);
      const delDate = initialData.delivery_date
        ? String(initialData.delivery_date).split(' ')[0]
        : mode === 'quote'
          ? (expDate || todayStr)
          : '';

      setSaleDate(invDate);
      setExpiryDate(expDate);
      setDeliveryDate(delDate);

      const ft = String(initialData.fulfillment_type || '').toLowerCase();
      if (ft === 'delivery') setOrderType('Delivery Price');
      else if (ft === 'pickup') setOrderType('Store Pickup');

      if (initialData.customer) {
        setSelectedCustomerId(initialData.customer.name || '');
        setCustomerName(initialData.customer.customer_name || initialData.customer.name || '');
        setSelectedCustomerType(initialData.customer.customer_type || 'Retailer');
        setAddress(initialData.customer.address || '');
        setCity(initialData.customer.city || '');
        setPostCode(initialData.customer.postal_code || '');
      }

      if (initialData.customer_birth_year) {
        setVerifiedCustomerBirthYear(Number(initialData.customer_birth_year) || null);
      }

      if (initialData.notes) {
        setNotes(initialData.notes);
      }

      if (initialData.payment_method && initialData.payment_method !== 'not set') {
        const pm = String(initialData.payment_method);
        // Legacy quote/sale values → Deposit labels
        if (pm === 'Cash') setPaymentMethod('Deposit Cash');
        else if (pm === 'Card') setPaymentMethod('Deposit Card');
        else setPaymentMethod(pm);
      }

      if (initialData.discount_amount) {
        setDiscountType('fixed');
        setDiscountValue(Number(initialData.discount_amount) || 0);
      }

      if (initialData.items && Array.isArray(initialData.items)) {
        setItems(
          initialData.items.map((it: any) => {
            let vatRate = vatRateFromItemTax(it.tax_rate);
            // Fallback: if API sent line vat + amount, recover rate (legacy edits)
            if (
              vatRate === 0 &&
              it.tax_rate == null &&
              Number(it.vat) > 0 &&
              Number(it.amount || it.rate) > 0
            ) {
              const base = Number(it.amount) || Number(it.quantity || 1) * Number(it.rate || 0);
              if (base > 0) vatRate = Number(it.vat) / base;
            }
            return {
              item: it.item || it.item_code,
              item_code: it.item_code || it.item,
              item_name: it.item_name || it.item_code || 'Item',
              line_comment: it.line_comment || '',
              uom: it.uom || 'Nos',
              cost_price: Number(it.cost_price) || 0,
              base_price: Number(it.rate) || 0,
              rate: Number(it.rate) || 0,
              stock: Number(it.available_stock) || 0,
              quantity: Number(it.quantity) || 1,
              vat_rate: vatRate,
            };
          })
        );
      }
    } else if (isOpen && !editDocId) {
      setSaleDate(todayStr);
      setExpiryDate(getDefaultExpiry(todayStr));
      setDeliveryDate(todayStr);
      setOrderType('Delivery Price');
      setPaymentTerm('0 Days');
      setPaymentMethod('Select payment method');
      handleClearCustomer();
      setNotes('');
      setDiscountType('percentage');
      setDiscountValue(0);
      setItems([]);
      setVerifiedCustomerBirthYear(null);
      setAgePendingProduct(null);
    }
  }, [isOpen, editDocId, initialData]);

  // Product Search State
  const [productSearch, setProductSearch] = useState<string>('');
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState<boolean>(false);
  const productInputRef = useRef<HTMLInputElement>(null);
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const productDropdownRef = useRef<HTMLDivElement>(null);

  // Keyboard shortcut Ctrl+N to focus product search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        productInputRef.current?.focus();
      }
      if (e.key === 'Escape' && isOpen) {
        if (isProductDropdownOpen) {
          setIsProductDropdownOpen(false);
        } else if (isCustomerDropdownOpen) {
          setIsCustomerDropdownOpen(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isProductDropdownOpen, isCustomerDropdownOpen, onClose]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target as Node)) {
        setIsCustomerDropdownOpen(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(e.target as Node)) {
        setIsProductDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch Customers Query
  const { data: customerList } = useQuery({
    queryKey: ['salesModalCustomers', customerSearch],
    queryFn: async () => {
      const res = await posApi.getCustomers({ search: customerSearch, limit: 30 });
      return res.data.message?.customers || res.data.message || [];
    },
    enabled: isOpen,
    staleTime: 30_000,
  });

  // Fetch Products Query
  const { data: productList } = useQuery({
    queryKey: ['salesModalProducts', productSearch],
    queryFn: async () => {
      const res = await posApi.getItems({ search: productSearch, limit: 30 });
      return res.data.message?.items || res.data.message || [];
    },
    enabled: isOpen && productSearch.trim().length > 0,
    staleTime: 30_000,
  });

  // Customer Type Badges
  const getCustomerTypeBadge = (type?: string, isDefaultCustomer?: boolean) => {
    const val = type || 'Retailer';
    let badge = null;
    if (val === 'Wholesale') {
      badge = (
        <span className="text-[9px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 shrink-0">
          Wholesale
        </span>
      );
    } else if (val === 'Distributor' || val === 'Distributer') {
      badge = (
        <span className="text-[9px] font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200 shrink-0">
          Distributor
        </span>
      );
    } else {
      badge = (
        <span className="text-[9px] font-semibold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200 shrink-0">
          Retailer
        </span>
      );
    }

    if (isDefaultCustomer) {
      return (
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded border border-emerald-300">
            Default
          </span>
          {badge}
        </div>
      );
    }
    return badge;
  };

  // Handle Customer Selection
  const handleSelectCustomer = (c: any) => {
    setSelectedCustomerId(c.name || '');
    setCustomerName(c.customer_name || c.name || '');
    setSelectedCustomerType(c.customer_type || 'Retailer');
    setIsSelectedDefault(c.is_default === 1);
    setCustomerSearch('');
    setAddress(c.address || '');
    setCity(c.city || '');
    setPostCode(c.postal_code || c.zip_code || '');

    const limit = Number(c.credit_limit || 0);
    const balance = Number(c.outstanding_balance || 0);
    setCustomerLimit(limit);
    setCustomerAvailable(Math.max(0, limit - balance));

    setIsCustomerDropdownOpen(false);
  };

  const handleClearCustomer = () => {
    setSelectedCustomerId('');
    setCustomerName('');
    setSelectedCustomerType('Retailer');
    setIsSelectedDefault(false);
    setCustomerSearch('');
    setAddress('');
    setCity('');
    setPostCode('');
    setCustomerLimit(0);
    setCustomerAvailable(0);
  };

  const handleCreateQuickCustomer = (name: string) => {
    setSelectedCustomerId('');
    setCustomerName(name);
    setSelectedCustomerType('Retailer');
    setIsSelectedDefault(false);
    setCustomerLimit(0);
    setCustomerAvailable(0);
    setCustomerSearch('');
    setIsCustomerDropdownOpen(false);
    toast.success(`Selected customer "${name}"`);
  };

  // Add Item to Line Items Table with Age Restriction compliance
  const handleAddItem = (prod: any) => {
    const itemDocName = prod.name || prod.item;
    const itemCode = prod.item_code || prod.name;
    const minAge = Number(prod.minimum_age) || 0;
    const currentYear = new Date().getFullYear();
    const isCustomerAlreadyVerified = verifiedCustomerBirthYear !== null && (currentYear - verifiedCustomerBirthYear) >= minAge;
    const alreadyInItems = items.some((it) => it.item === itemDocName || it.item_code === itemCode);

    if (
      mode === 'sale' &&
      Boolean(prod.has_age_restriction) &&
      minAge > 0 &&
      !isCustomerAlreadyVerified &&
      !alreadyInItems
    ) {
      setAgePendingProduct(prod);
      return;
    }

    addItemToOrder(prod);
  };

  const addItemToOrder = (prod: any, birthYear?: number) => {
    const itemDocName = prod.name || prod.item;
    const itemCode = prod.item_code || prod.name;
    const existingIndex = items.findIndex((it) => it.item === itemDocName || it.item_code === itemCode);

    if (existingIndex >= 0) {
      // Increase qty by 1
      setItems((prev) =>
        prev.map((it, idx) => (idx === existingIndex ? { ...it, quantity: it.quantity + 1 } : it))
      );
      toast.success(`Increased ${prod.item_name || itemCode} quantity`);
    } else {
      const salePrice = Number(prod.sale_price || prod.rate || 0);
      const costPrice = Number(prod.cost_price || 0);
      const stock = Number(prod.stock_quantity ?? prod.actual_qty ?? 0);
      const uom = prod.unit_of_measurement || prod.stock_uom || 'Unit';

      const newItem: OrderLineItem = {
        item: itemDocName,
        item_code: itemCode,
        item_name: prod.item_name || itemCode,
        uom,
        cost_price: costPrice,
        base_price: salePrice,
        rate: salePrice,
        stock,
        quantity: 1,
        vat_rate: vatRateFromItemTax(prod.tax_rate),
        has_age_restriction: Boolean(prod.has_age_restriction),
        minimum_age: Number(prod.minimum_age) || 0,
        verified_birth_year: birthYear || verifiedCustomerBirthYear || undefined,
      };
      setItems((prev) => [...prev, newItem]);
      toast.success(`Added ${prod.item_name || itemCode}`);
    }

    setProductSearch('');
    setIsProductDropdownOpen(false);
  };

  const handleAgeConfirm = (birthYear: number) => {
    if (agePendingProduct) {
      const prod = agePendingProduct;
      setVerifiedCustomerBirthYear(birthYear);
      setAgePendingProduct(null);
      toast.success(`Age verified (${birthYear}) for ${prod.item_name || prod.item_code}`);
      addItemToOrder(prod, birthYear);
    }
  };

  const handleAgeCancel = () => {
    if (agePendingProduct) {
      toast.error(`Sale cancelled: ${agePendingProduct.item_name || agePendingProduct.item_code} requires customer age verification`);
    }
    setAgePendingProduct(null);
  };

  // Update item field
  const handleUpdateItem = (index: number, field: keyof OrderLineItem, val: number) => {
    setItems((prev) =>
      prev.map((item, idx) => {
        if (idx !== index) return item;
        return {
          ...item,
          [field]: Math.max(0, val),
        };
      })
    );
  };

  // Remove item
  const handleRemoveItem = (index: number) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  // Calculated Totals
  const { subtotal, vatTotal, discountAmount, grandTotal, totalProfit, overallMargin, totalQty } =
    useMemo(() => {
      let sub = 0;
      let vat = 0;
      let costSum = 0;
      let qty = 0;

      items.forEach((it) => {
        const lineSub = it.quantity * it.rate;
        const lineVat = lineSub * it.vat_rate;
        const lineCost = it.quantity * it.cost_price;

        sub += lineSub;
        vat += lineVat;
        costSum += lineCost;
        qty += it.quantity;
      });

      let disc = 0;
      if (discountType === 'percentage') {
        disc = (sub * Math.max(0, discountValue)) / 100;
      } else {
        disc = Math.min(sub, Math.max(0, discountValue));
      }

      const total = Math.max(0, sub - disc + vat);
      const profit = Math.max(0, sub - disc - costSum);
      const margin = sub > 0 ? (profit / sub) * 100 : 0;

      return {
        subtotal: sub,
        vatTotal: vat,
        discountAmount: disc,
        grandTotal: total,
        totalProfit: profit,
        overallMargin: margin,
        totalQty: qty,
      };
    }, [items, discountType, discountValue]);

  // Submit Mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      if (items.length === 0) {
        throw new Error('Please add at least one product to the order.');
      }
      if (!customerName.trim() && !selectedCustomerId) {
        throw new Error('Customer name is required.');
      }

      const payload = {
        doc_name: editDocId || undefined,
        name: editDocId || undefined,
        type: mode, // 'quote' or 'sale'
        customer: selectedCustomerId,
        customer_name: customerName,
        customer_birth_year: verifiedCustomerBirthYear || undefined,
        delivery_date: deliveryDate,
        sale_date: saleDate,
        expiry_date: expiryDate,
        valid_till: expiryDate,
        order_type: orderType,
        payment_term: paymentTerm,
        // SD-UK-034: do not invent Cash when user left payment unset
        payment_method: paymentMethod === 'Select payment method' ? '' : paymentMethod,
        address,
        city,
        postal_code: postCode,
        delivery_address_same: deliverySameAsAbove,
        delivery_address: deliverySameAsAbove ? address : deliveryAddress,
        delivery_city: deliverySameAsAbove ? city : deliveryCity,
        delivery_postal_code: deliverySameAsAbove ? postCode : deliveryPostCode,
        notes,
        discount_amount: discountAmount,
        discount_type: discountType,
        discount_value: discountValue,
        items: items.map((it) => ({
          item: it.item,
          item_code: it.item_code,
          item_name: it.item_name,
          line_comment: it.line_comment || '',
          quantity: it.quantity,
          rate: it.rate,
          cost_price: it.cost_price,
          vat: it.quantity * it.rate * it.vat_rate,
          amount: it.quantity * it.rate,
          customer_birth_year: it.has_age_restriction ? (it.verified_birth_year || verifiedCustomerBirthYear) : undefined,
        })),
      };

      const res = await salesHubApi.createRecord(payload);
      return res.data?.message || res.data;
    },
    onSuccess: (data) => {
      const docName = editDocId || data?.name || '';
      toast.success(
        editDocId
          ? `${mode === 'quote' ? 'Quote' : 'Sale'} ${docName} updated successfully!`
          : `${mode === 'quote' ? 'Quote' : 'Sale'} ${docName} created successfully!`
      );
      queryClient.invalidateQueries({ queryKey: ['salesHubCounts'] });
      queryClient.invalidateQueries({ queryKey: ['salesHubList'] });
      queryClient.invalidateQueries({ queryKey: ['salesHubDetail'] });
      onSuccess?.(docName, mode);
      onClose();
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || err.message || 'Failed to create record';
      toast.error(msg);
    },
  });

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 z-30 bg-slate-50 flex flex-col w-full h-full overflow-hidden animate-in fade-in duration-150">
      {/* FULL SCREEN HEADER */}
      <div className="px-6 py-3.5 border-b border-slate-200/90 flex items-center justify-between shrink-0 bg-white shadow-2xs z-10">
        <div className="max-w-7xl mx-auto w-full flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleSidebar}
              className="lg:hidden p-1.5 -ml-1 text-slate-600 hover:bg-slate-100 rounded-lg shrink-0"
              title="Toggle Sidebar"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
                  {editDocId
                    ? mode === 'quote'
                      ? `Edit Quote (${editDocId})`
                      : `Edit Sale (${editDocId})`
                    : mode === 'quote'
                    ? 'Create a new quote'
                    : 'Create a new sale'}
                </h2>
                <span className={`text-[11px] px-2.5 py-0.5 rounded-full font-semibold border ${
                  mode === 'quote'
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                }`}>
                  {mode === 'quote' ? 'Quotation' : 'Direct Sale'}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">All fields marked with a * are required.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors border border-slate-200 shadow-2xs"
              title="Close (Esc)"
            >
              <X className="w-4 h-4" />
              <span>Close</span>
            </button>
          </div>
        </div>
      </div>

        {/* FULL SCREEN BODY (SCROLLABLE) */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6 bg-slate-50/50">
          <div className="max-w-7xl mx-auto space-y-6">
          {/* 1. ORDER DETAILS (COLLAPSIBLE) */}
          <div className="bg-white rounded-xl border border-slate-200/80 shadow-2xs overflow-hidden">
            <div
              onClick={() => setIsOrderDetailsOpen(!isOrderDetailsOpen)}
              className="px-5 py-3.5 bg-white flex items-center justify-between cursor-pointer select-none hover:bg-slate-50/60 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-slate-900 tracking-tight">Order Details</span>
                {verifiedCustomerBirthYear && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <ShieldAlert className="w-3 h-3 text-emerald-600" />
                    Age Verified ({verifiedCustomerBirthYear})
                  </span>
                )}
              </div>
              <button type="button" className="text-slate-400 hover:text-slate-600">
                {isOrderDetailsOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            </div>

            {isOrderDetailsOpen && (
              <div className="p-5 pt-1 border-t border-slate-100 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Left Column */}
                  <div className="space-y-4">
                    {/* Pick delivery date */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Pick delivery date
                        {deliveryDate && deliveryDate > todayStr && (
                          <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                            Future
                          </span>
                        )}
                      </label>
                      <div className="relative">
                        <Calendar className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${deliveryDate && deliveryDate > todayStr ? 'text-amber-500' : 'text-slate-400'}`} />
                        <input
                          type="date"
                          value={deliveryDate}
                          onChange={(e) => setDeliveryDate(e.target.value)}
                          className={`w-full pl-9 pr-8 py-2 text-xs font-medium rounded-lg focus:outline-none focus:ring-2 transition-all ${
                            deliveryDate && deliveryDate > todayStr
                              ? 'text-amber-950 bg-amber-50 border-2 border-amber-400 focus:ring-amber-500/20 focus:border-amber-500'
                              : 'text-slate-800 bg-white border border-slate-200 focus:ring-rose-500/20 focus:border-rose-500'
                          }`}
                        />
                        {deliveryDate && (
                          <button
                            type="button"
                            onClick={() => setDeliveryDate('')}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-rose-400 hover:text-rose-600 p-0.5"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Order type * */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Order type <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <select
                          value={orderType}
                          onChange={(e) => setOrderType(e.target.value)}
                          className="w-full px-3 py-2 text-xs font-medium text-slate-800 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 appearance-none pr-8 transition-all"
                        >
                          <option value="Delivery Price">Delivery Price</option>
                          <option value="Collection">Collection</option>
                          <option value="Store Pickup">Store Pickup</option>
                          <option value="Standard Order">Standard Order</option>
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      </div>
                    </div>


                    {/* Address * */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Address <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Street address..."
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className="w-full px-3 py-2 text-xs font-medium text-slate-800 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                      />
                    </div>

                    {/* Post code * */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Post code <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Post code (e.g. S7 1FS)..."
                        value={postCode}
                        onChange={(e) => setPostCode(e.target.value)}
                        className="w-full px-3 py-2 text-xs font-medium text-slate-800 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    {/* Sale / Quote date & Expiry date */}
                    {mode === 'quote' ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 mb-1">
                            Quote date
                          </label>
                          <div className="relative">
                            <Calendar className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                            <input
                              type="date"
                              value={saleDate}
                              onChange={(e) => setSaleDate(e.target.value)}
                              className="w-full pl-9 pr-3 py-2 text-xs font-medium text-slate-800 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-slate-700 mb-1">
                            Expiry date <span className="text-rose-500">*</span>
                          </label>
                          <div className="relative">
                            <Calendar className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                            <input
                              type="date"
                              value={expiryDate}
                              onChange={(e) => setExpiryDate(e.target.value)}
                              className="w-full pl-9 pr-3 py-2 text-xs font-medium text-slate-800 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                          Sale date
                          {saleDate && saleDate > todayStr && (
                            <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-sky-700">
                              Future
                            </span>
                          )}
                        </label>
                        <div className="relative">
                          <Calendar className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${saleDate && saleDate > todayStr ? 'text-sky-500' : 'text-slate-400'}`} />
                          <input
                            type="date"
                            value={saleDate}
                            onChange={(e) => setSaleDate(e.target.value)}
                            className={`w-full pl-9 pr-3 py-2 text-xs font-medium rounded-lg focus:outline-none focus:ring-2 transition-all ${
                              saleDate && saleDate > todayStr
                                ? 'text-sky-950 bg-sky-50 border-2 border-sky-400 focus:ring-sky-500/20 focus:border-sky-500'
                                : 'text-slate-800 bg-white border border-slate-200 focus:ring-rose-500/20 focus:border-rose-500'
                            }`}
                          />
                        </div>
                      </div>
                    )}

                    {/* Merged Customer Field (matches system style with search & badges) */}
                    <div ref={customerDropdownRef} className="relative">
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-semibold text-slate-700">
                          Customer <span className="text-rose-500">*</span>
                        </label>
                        <div className="text-[11px] font-semibold flex items-center gap-2">
                          <span className="text-slate-500">
                            Limit: {formatMoney(customerLimit)}
                          </span>
                          <span className="text-emerald-600">
                            Available: {formatMoney(customerAvailable)}
                          </span>
                        </div>
                      </div>

                      {/* Combobox Trigger Button */}
                      <button
                        type="button"
                        onClick={() => setIsCustomerDropdownOpen((prev) => !prev)}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-slate-200 bg-white rounded-lg text-xs hover:bg-slate-50 transition-colors shadow-2xs cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <User className="w-4 h-4 text-slate-400 shrink-0" />
                          <span className={`font-semibold truncate ${customerName ? 'text-slate-900' : 'text-slate-400'}`}>
                            {customerName || 'Select or search customer...'}
                          </span>
                          {customerName && getCustomerTypeBadge(selectedCustomerType, isSelectedDefault)}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {customerName && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClearCustomer();
                              }}
                              className="p-0.5 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-600 cursor-pointer"
                              title="Clear customer"
                            >
                              <X className="w-3.5 h-3.5" />
                            </span>
                          )}
                          <ChevronDown
                            className={`w-3.5 h-3.5 text-slate-400 transition-transform ${
                              isCustomerDropdownOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                      </button>

                      {/* Dropdown Menu with Search Input */}
                      {isCustomerDropdownOpen && (
                        <div className="absolute left-0 right-0 top-full mt-1.5 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden flex flex-col max-h-80 animate-in fade-in zoom-in-95 duration-100">
                          {/* Search Header */}
                          <div className="p-2 border-b border-slate-100 flex items-center gap-2 shrink-0 bg-slate-50/70">
                            <div className="relative flex-1">
                              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                              <input
                                type="text"
                                value={customerSearch}
                                onChange={(e) => setCustomerSearch(e.target.value)}
                                className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 placeholder:text-slate-400"
                                placeholder="Search customer by name or phone..."
                                autoFocus
                              />
                            </div>
                          </div>

                          {/* Customer List */}
                          <div className="overflow-y-auto flex-1 p-1 space-y-0.5 max-h-56">
                            {(customerList || []).map((c: any) => {
                              const isSelected = selectedCustomerId === c.name || customerName === c.customer_name;
                              const isDefault = c.is_default === 1;

                              return (
                                <button
                                  key={c.name}
                                  type="button"
                                  onClick={() => handleSelectCustomer(c)}
                                  className={`w-full text-left px-3 py-2 text-xs rounded-lg hover:bg-slate-50 flex flex-col gap-0.5 cursor-pointer transition-colors ${
                                    isSelected
                                      ? 'bg-rose-50/80 text-rose-800 font-semibold border border-rose-200'
                                      : isDefault
                                      ? 'bg-emerald-50/30 text-slate-800 border border-emerald-100/60'
                                      : 'text-slate-700 border border-transparent'
                                  }`}
                                >
                                  <div className="flex items-center justify-between w-full">
                                    <span className="truncate font-semibold text-slate-900">
                                      {c.customer_name || c.name}
                                    </span>
                                    {getCustomerTypeBadge(c.customer_type, isDefault)}
                                  </div>
                                  <div className="flex items-center justify-between text-[11px] text-slate-400">
                                    <span>{c.mobile || c.city || 'No mobile'}</span>
                                    {c.credit_limit > 0 && (
                                      <span className="text-emerald-600 font-semibold">
                                        Limit: {formatMoney(c.credit_limit)}
                                      </span>
                                    )}
                                  </div>
                                </button>
                              );
                            })}

                            {(customerList || []).length === 0 && (
                              <div className="p-4 text-center text-xs text-slate-400">
                                No customers found
                              </div>
                            )}
                          </div>

                          {/* Quick Create / Use Name Option */}
                          {customerSearch.trim() !== '' && (
                            <div className="p-1.5 bg-slate-50 border-t border-slate-100 shrink-0">
                              <button
                                type="button"
                                onClick={() => handleCreateQuickCustomer(customerSearch.trim())}
                                className="w-full py-1.5 text-center text-xs text-rose-600 hover:text-rose-700 font-semibold hover:bg-white rounded-lg border border-dashed border-rose-200 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                <span>Use &quot;{customerSearch.trim()}&quot; as Customer</span>
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Payment Term & Payment Method */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                          Payment term
                        </label>
                        <div className="relative">
                          <select
                            value={paymentTerm}
                            onChange={(e) => setPaymentTerm(e.target.value)}
                            className="w-full px-3 py-2 text-xs font-medium text-slate-800 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 appearance-none pr-7 transition-all"
                          >
                            <option value="0 Days">0 Days</option>
                            <option value="7 Days">7 Days</option>
                            <option value="14 Days">14 Days</option>
                            <option value="30 Days">30 Days</option>
                            <option value="60 Days">60 Days</option>
                          </select>
                          <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                          Payment method
                        </label>
                        <div className="relative">
                          <select
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            className="w-full px-3 py-2 text-xs font-medium text-slate-800 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 appearance-none pr-7 transition-all"
                          >
                            <option value="Select payment method">Select payment method</option>
                            <option value="Deposit Cash">Deposit Cash</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                            <option value="Deposit Card">Deposit Card</option>
                            <option value="Credit">Credit Account</option>
                          </select>
                          <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                      </div>
                    </div>

                    {/* City */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">City</label>
                      <input
                        type="text"
                        placeholder="City (e.g. Sheffield)..."
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="w-full px-3 py-2 text-xs font-medium text-slate-800 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Delivery address same as above checkbox */}
                <div className="pt-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none text-xs font-semibold text-slate-800">
                    <input
                      type="checkbox"
                      checked={deliverySameAsAbove}
                      onChange={(e) => setDeliverySameAsAbove(e.target.checked)}
                      className="w-4 h-4 rounded text-rose-600 focus:ring-rose-500 border-slate-300"
                    />
                    <span>Delivery address same as above</span>
                  </label>

                  {!deliverySameAsAbove && (
                    <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200/80 grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                          Delivery Address
                        </label>
                        <input
                          type="text"
                          placeholder="Delivery address..."
                          value={deliveryAddress}
                          onChange={(e) => setDeliveryAddress(e.target.value)}
                          className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-200 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                          Delivery City
                        </label>
                        <input
                          type="text"
                          placeholder="Delivery city..."
                          value={deliveryCity}
                          onChange={(e) => setDeliveryCity(e.target.value)}
                          className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-200 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                          Delivery Post code
                        </label>
                        <input
                          type="text"
                          placeholder="Delivery post code..."
                          value={deliveryPostCode}
                          onChange={(e) => setDeliveryPostCode(e.target.value)}
                          className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-200 rounded-md"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Notes</label>
                  <textarea
                    rows={2}
                    placeholder="Order notes, delivery instructions, or customer remarks..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-2 text-xs font-medium text-slate-800 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all resize-y"
                  />
                </div>
              </div>
            )}
          </div>

          {/* 2. DISCOUNTS (COLLAPSIBLE) */}
          <div className="bg-white rounded-xl border border-slate-200/80 shadow-2xs overflow-hidden">
            <div
              onClick={() => setIsDiscountsOpen(!isDiscountsOpen)}
              className="px-5 py-3.5 bg-white flex items-center justify-between cursor-pointer select-none hover:bg-slate-50/60 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-900 tracking-tight">Discounts</span>
                {discountAmount > 0 && (
                  <span className="text-[11px] font-semibold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200">
                    -{formatMoney(discountAmount)}
                  </span>
                )}
              </div>
              <button type="button" className="text-slate-400 hover:text-slate-600">
                {isDiscountsOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            </div>

            {isDiscountsOpen && (
              <div className="p-5 pt-2 border-t border-slate-100 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-700">Type:</span>
                  <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
                    <button
                      type="button"
                      onClick={() => setDiscountType('percentage')}
                      className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                        discountType === 'percentage'
                          ? 'bg-white shadow-2xs text-slate-900'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      Percentage (%)
                    </button>
                    <button
                      type="button"
                      onClick={() => setDiscountType('fixed')}
                      className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                        discountType === 'fixed'
                          ? 'bg-white shadow-2xs text-slate-900'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      Fixed Amount ({currencySymbol})
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-700">Value:</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(Number(e.target.value) || 0)}
                    className="w-28 px-3 py-1.5 text-xs font-bold text-slate-800 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* 3. ADD PRODUCTS * */}
          <div className="bg-white rounded-xl border border-slate-200/80 shadow-2xs p-5 space-y-4">
            {/* Header line with Visual/Compact toggle */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-900 tracking-tight">
                Add products <span className="text-rose-500">*</span>
              </span>

              <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-100/60">
                <button
                  type="button"
                  onClick={() => setViewMode('visual')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                    viewMode === 'visual'
                      ? 'bg-slate-900 text-white shadow-2xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Layers className="w-3.5 h-3.5" />
                  <span>Visual</span>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('compact')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                    viewMode === 'compact'
                      ? 'bg-slate-900 text-white shadow-2xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  <span>Compact</span>
                </button>
              </div>
            </div>

            {/* Product Search Input with Ctrl N shortcut badge */}
            <div ref={productDropdownRef} className="relative">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  ref={productInputRef}
                  type="text"
                  placeholder="Search for a product by name, barcode, or code..."
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setIsProductDropdownOpen(true);
                  }}
                  onFocus={() => {
                    if (productSearch.trim().length > 0) setIsProductDropdownOpen(true);
                  }}
                  className="w-full pl-10 pr-16 py-2.5 text-xs font-medium text-slate-800 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 shadow-2xs transition-all placeholder:text-slate-400"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-[10px] font-mono font-semibold text-slate-400 bg-slate-100 rounded border border-slate-200">
                  Ctrl N
                </span>
              </div>

              {/* Product Autocomplete Dropdown */}
              {isProductDropdownOpen && productSearch.trim().length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl max-h-64 overflow-y-auto z-40 py-1">
                  {(productList || []).length === 0 ? (
                    <div className="p-4 text-center text-xs text-slate-400">
                      No products found matching "{productSearch}"
                    </div>
                  ) : (
                    (productList || []).map((p: any) => {
                      const itemCode = p.item_code || p.name;
                      const stock = Number(p.stock_quantity ?? p.actual_qty ?? 0);
                      const price = Number(p.sale_price || p.rate || 0);

                      return (
                        <button
                          key={itemCode}
                          type="button"
                          onClick={() => handleAddItem(p)}
                          className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-center justify-between text-xs transition-colors border-b border-slate-50 last:border-0"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center font-bold text-slate-500 text-xs shrink-0 overflow-hidden">
                              {p.image ? (
                                <img
                                  src={p.image}
                                  alt={p.item_name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                (p.item_name || itemCode).charAt(0).toUpperCase()
                              )}
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                                <span>{p.item_name || itemCode}</span>
                                {Boolean(p.has_age_restriction) && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1 shrink-0">
                                    <ShieldAlert className="w-2.5 h-2.5 text-amber-700" />
                                    {p.minimum_age || 18}+
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-400 flex items-center gap-2">
                                <span>Code: {itemCode}</span>
                                {p.barcode && <span>• Barcode: {p.barcode}</span>}
                              </div>
                            </div>
                          </div>

                          <div className="text-right">
                            <div className="font-bold text-slate-900">{formatMoney(price)}</div>
                            <div
                              className={`text-[11px] font-semibold ${
                                stock <= 0 ? 'text-rose-500' : 'text-emerald-600'
                              }`}
                            >
                              Stock: {stock}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Toggle Details button */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowDetails(!showDetails)}
                className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors shadow-2xs"
              >
                {showDetails ? 'Hide Details' : 'Show Details'}
              </button>
            </div>

            {/* PRODUCT LINE ITEMS TABLE */}
            <div className="overflow-x-auto border border-slate-200/80 rounded-xl">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200/80 text-slate-500 font-semibold uppercase text-[11px] tracking-wider">
                    <th className="py-2.5 px-3">Product</th>
                    <th className="py-2.5 px-3 text-center">Price</th>
                    <th className="py-2.5 px-3 text-center">Stock</th>
                    <th className="py-2.5 px-3 text-center w-24">Quantity</th>
                    <th className="py-2.5 px-3 text-center w-28">Sale Price</th>
                    {showDetails && (
                      <>
                        <th className="py-2.5 px-3 text-right">VAT</th>
                        <th className="py-2.5 px-3 text-right">Subtotal</th>
                        <th className="py-2.5 px-3 text-right">Total</th>
                        {/* <th className="py-2.5 px-3 text-right">Profit</th>
                        <th className="py-2.5 px-3 text-right">Profit Margin</th> */}
                      </>
                    )}
                    <th className="py-2.5 px-3 text-center w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={showDetails ? 9 : 6}
                        className="py-8 text-center text-slate-400 font-medium"
                      >
                        No products added yet. Use the search bar above to add products.
                      </td>
                    </tr>
                  ) : (
                    items.map((item, index) => {
                      const lineSub = item.quantity * item.rate;
                      const lineVat = lineSub * item.vat_rate;
                      const lineTotal = lineSub + lineVat;
                      const lineCost = item.quantity * item.cost_price;
                      void lineCost;
                      // const lineProfit = lineSub - lineCost;
                      // const lineMargin = lineSub > 0 ? (lineProfit / lineSub) * 100 : 0;

                      return (
                        <tr key={`${item.item_code}-${index}`} className="hover:bg-slate-50/50">
                          {/* Product */}
                          <td className="py-3 px-3">
                            <div className="flex items-start gap-2">
                              <RotateCcw className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-slate-900 flex items-start gap-1.5">
                                  <span className="min-w-0">{item.item_name}</span>
                                  {Boolean(item.has_age_restriction) && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1 shrink-0">
                                      <ShieldAlert className="w-2.5 h-2.5 text-amber-600" />
                                      {item.minimum_age || 18}+
                                    </span>
                                  )}
                                  <span className="inline-flex items-center gap-0.5 shrink-0 ml-auto">
                                    <button
                                      type="button"
                                      title={item.line_comment ? 'Edit comment' : 'Add comment'}
                                      onClick={() => setLineNoteEdit({ index, mode: 'comment' })}
                                      className={`p-1 rounded ${item.line_comment ? 'text-rose-600 bg-rose-50' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'}`}
                                    >
                                      <MessageSquare className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      title="Rename for this document"
                                      onClick={() => setLineNoteEdit({ index, mode: 'rename' })}
                                      className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                  </span>
                                </div>
                                {item.line_comment ? (
                                  <p className="text-[11px] text-slate-600 italic mt-0.5 line-clamp-2">{item.line_comment}</p>
                                ) : null}
                                <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
                                  <span>{item.uom}</span>
                                  <Copy className="w-3 h-3 text-slate-400 cursor-pointer hover:text-slate-600" />
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Price (cost / base price indicator) */}
                          <td className="py-3 px-3 text-center">
                            <div className="text-slate-400 text-[11px] line-through">
                              {formatMoney(item.cost_price)}
                            </div>
                            <div className="font-medium text-slate-700">
                              {formatMoney(item.base_price)}
                            </div>
                          </td>

                          {/* Stock */}
                          <td className="py-3 px-3 text-center">
                            <span
                              className={`font-semibold ${
                                item.stock <= 0 ? 'text-rose-500 font-bold' : 'text-slate-700'
                              }`}
                            >
                              {item.stock}
                            </span>
                          </td>

                          {/* Quantity */}
                          <td className="py-3 px-3 text-center">
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={item.quantity}
                              onChange={(e) =>
                                handleUpdateItem(index, 'quantity', Number(e.target.value) || 1)
                              }
                              className="w-16 px-2 py-1 text-center font-bold text-slate-900 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
                            />
                          </td>

                          {/* Sale Price */}
                          <td className="py-3 px-3 text-center">
                            <div className="relative inline-flex items-center">
                              <span className="absolute left-2 text-xs font-semibold text-slate-400">
                                {currencySymbol}
                              </span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.rate}
                                onChange={(e) =>
                                  handleUpdateItem(index, 'rate', Number(e.target.value) || 0)
                                }
                                className="w-24 pl-5 pr-2 py-1 text-right font-bold text-slate-900 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
                              />
                            </div>
                          </td>

                          {/* Show details columns */}
                          {showDetails && (
                            <>
                              <td className="py-3 px-3 text-right font-medium text-slate-700">
                                {formatMoney(lineVat)}
                              </td>
                              <td className="py-3 px-3 text-right font-medium text-slate-800">
                                {formatMoney(lineSub)}
                              </td>
                              <td className="py-3 px-3 text-right font-bold text-slate-900">
                                {formatMoney(lineTotal)}
                              </td>
                              {/* <td className="py-3 px-3 text-right font-semibold text-emerald-600">
                                {formatMoney(lineProfit)}
                              </td>
                              <td className="py-3 px-3 text-right font-semibold text-slate-700">
                                {lineMargin.toFixed(0)}%
                              </td> */}
                            </>
                          )}

                          {/* Action */}
                          <td className="py-3 px-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(index)}
                              className="text-rose-400 hover:text-rose-600 p-1 rounded transition-colors"
                              title="Remove item"
                            >
                              <MinusCircle className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* FULL SCREEN FOOTER WITH TOTALS & ACTIONS */}
      <div className="px-6 py-3.5 border-t border-slate-200/90 bg-white flex flex-wrap items-center justify-between gap-4 shrink-0 shadow-xs z-10">
        <div className="max-w-7xl mx-auto w-full flex flex-wrap items-center justify-between gap-4">
          {/* Summary KPIs */}
          <div className="flex flex-wrap items-center gap-6 text-xs">
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-semibold">Items</span>
              <span className="font-bold text-slate-800">
                {items.length} ({totalQty} units)
              </span>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-semibold">
                Subtotal
              </span>
              <span className="font-bold text-slate-800">{formatMoney(subtotal)}</span>
            </div>
            {discountAmount > 0 && (
              <div>
                <span className="text-slate-400 block text-[10px] uppercase font-semibold">
                  Discount
                </span>
                <span className="font-bold text-rose-600">-{formatMoney(discountAmount)}</span>
              </div>
            )}
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-semibold">
                VAT
              </span>
              <span className="font-bold text-slate-800">{formatMoney(vatTotal)}</span>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-semibold">
                Grand Total
              </span>
              <span className="font-extrabold text-base text-slate-900">
                {formatMoney(grandTotal)}
              </span>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-semibold">
                Est. Profit
              </span>
              <span className="font-bold text-emerald-600">
                {formatMoney(totalProfit)} ({overallMargin.toFixed(0)}%)
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={createMutation.isPending}
              className="px-4 py-2 border border-slate-200 hover:border-slate-300 text-slate-700 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending || items.length === 0}
              className="bg-slate-900 hover:bg-black text-white px-5 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 shadow-sm transition-colors disabled:opacity-50"
            >
              {createMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              <span>
                {editDocId
                  ? mode === 'quote'
                    ? 'Update quote'
                    : 'Update sale'
                  : mode === 'quote'
                  ? 'Create quote'
                  : 'Create sale'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {agePendingProduct && (
        <AgeVerificationModal
          isOpen={Boolean(agePendingProduct)}
          itemName={agePendingProduct.item_name || agePendingProduct.item_code}
          minimumAge={Number(agePendingProduct.minimum_age) || 18}
          onCancel={handleAgeCancel}
          onConfirm={handleAgeConfirm}
        />
      )}
      {lineNoteEdit != null && items[lineNoteEdit.index] && (
        <CartLineNoteModal
          mode={lineNoteEdit.mode}
          itemName={items[lineNoteEdit.index].item_name}
          initialValue={
            lineNoteEdit.mode === 'rename'
              ? items[lineNoteEdit.index].item_name
              : items[lineNoteEdit.index].line_comment || ''
          }
          onClose={() => setLineNoteEdit(null)}
          onSave={(value) => {
            setItems((prev) =>
              prev.map((it, idx) => {
                if (idx !== lineNoteEdit.index) return it;
                if (lineNoteEdit.mode === 'rename') {
                  return { ...it, item_name: value.trim() || it.item_name };
                }
                return { ...it, line_comment: value.trim() };
              }),
            );
          }}
        />
      )}
    </div>
  );
}
